import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { Dropdown, Spinner, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { useMatchMedia } from '../hooks/useMatchMedia';
import { NotificationItem, notificationService } from '../services/api';
import { formatNotificationTimestamp } from '../utils/formatNotificationTimestamp';
import './NotificationBell.css';

/** Bootstrap `lg` — mesmo critério do sino no toolbar mobile da Home. */
const MOBILE_DROPDOWN_QUERY = '(max-width: 991.98px)';
const MOBILE_MENU_MAX_WIDTH_PX = 360;
const MOBILE_MENU_MARGIN_PX = 12;
const MOBILE_MENU_GAP_BELOW_TOGGLE_PX = 8;

function clearMobileFixedMenuStyles(menu: HTMLElement) {
  menu.style.removeProperty('position');
  menu.style.removeProperty('inset');
  menu.style.removeProperty('left');
  menu.style.removeProperty('right');
  menu.style.removeProperty('top');
  menu.style.removeProperty('width');
  menu.style.removeProperty('max-width');
  menu.style.removeProperty('transform');
}

export interface NotificationBellProps {
  unreadCount: number;
  onUnreadCountChange: (count: number) => void;
  className?: string;
}

const BELL_PREVIEW_MAX_CHARS = 41;

function truncateForBellPreview(text: string): { short: string; wasTruncated: boolean } {
  const t = text.trim();
  if (t.length <= BELL_PREVIEW_MAX_CHARS) return { short: t, wasTruncated: false };
  return { short: `${t.slice(0, BELL_PREVIEW_MAX_CHARS)}...`, wasTruncated: true };
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  unreadCount,
  onUnreadCountChange,
  className = '',
}) => {
  const { t, i18n } = useSafeTranslation();
  const navigate = useNavigate();
  const uid = useId();
  const toggleId = `notification-bell-dropdown-${uid}`;
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isNarrowViewport = useMatchMedia(MOBILE_DROPDOWN_QUERY);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const refreshUnread = useCallback(async () => {
    try {
      const { unreadCount: n } = await notificationService.getUnreadCount();
      onUnreadCountChange(n);
    } catch {
      /* ignore */
    }
  }, [onUnreadCountChange]);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notificationService.getNotifications({ take: 3 });
      setItems(data.items);
      setTotalCount(data.totalCount);
    } catch {
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleToggle = (next: boolean) => {
    setOpen(next);
    if (next) {
      void loadPreview();
      void refreshUnread();
    }
  };

  /** Mobile: menu largo + align end faz o Popper cortar à esquerda — centraliza no viewport. */
  useLayoutEffect(() => {
    const menu = menuRef.current;
    const toggle = toggleRef.current;
    if (!menu) return;

    if (!open || !isNarrowViewport) {
      clearMobileFixedMenuStyles(menu);
      return;
    }

    const apply = () => {
      if (!toggle) return;
      const tRect = toggle.getBoundingClientRect();
      const vw = window.innerWidth;
      const w = Math.min(MOBILE_MENU_MAX_WIDTH_PX, vw - MOBILE_MENU_MARGIN_PX * 2);
      const left = (vw - w) / 2;
      menu.style.setProperty('position', 'fixed', 'important');
      menu.style.setProperty('left', `${left}px`, 'important');
      menu.style.setProperty('right', 'auto', 'important');
      menu.style.setProperty('width', `${w}px`, 'important');
      menu.style.setProperty('max-width', `${w}px`, 'important');
      menu.style.setProperty('transform', 'none', 'important');
      menu.style.setProperty('top', `${tRect.bottom + MOBILE_MENU_GAP_BELOW_TOGGLE_PX}px`, 'important');
      menu.style.setProperty('bottom', 'auto', 'important');
    };

    apply();
    window.addEventListener('resize', apply);
    window.addEventListener('scroll', apply, true);
    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('scroll', apply, true);
      clearMobileFixedMenuStyles(menu);
    };
  }, [open, isNarrowViewport, loading, items, totalCount]);

  const handleItemActivate = async (n: NotificationItem) => {
    if (!n.readAt) {
      try {
        await notificationService.markRead(n.id);
        await refreshUnread();
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    navigate('/notifications');
  };

  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount);
  const ariaLabel =
    unreadCount > 0
      ? `${t('notifications.ariaBell', 'Notifications')}, ${unreadCount} ${t('notifications.unread', 'unread')}`
      : t('notifications.ariaBell', 'Notifications');

  return (
    <div className={`notification-bell-wrap position-relative ${className}`.trim()}>
      <Dropdown align="end" show={open} onToggle={handleToggle}>
        <Dropdown.Toggle
          ref={toggleRef}
          id={toggleId}
          variant="link"
          className="notification-bell-toggle px-2 py-1"
          aria-label={ariaLabel}
        >
          <i className="bi bi-bell notification-bell-icon" aria-hidden />
          {unreadCount > 0 && (
            <Badge pill bg="danger" className="notification-bell-badge">
              {badgeText}
            </Badge>
          )}
        </Dropdown.Toggle>
        <Dropdown.Menu
          ref={menuRef}
          className="notification-dropdown-menu shadow"
          renderOnMount
        >
          <div className="notification-dropdown-header">
            {t('notifications.inboxTitle', 'Notifications')}
          </div>
          {loading && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="notification-dropdown-empty">
              {t('notifications.emptyPreview', 'No notifications yet.')}
            </div>
          )}
          {!loading &&
            items.map((n) => {
              const titlePrev = truncateForBellPreview(n.title ?? '');
              const bodyPrev = n.body ? truncateForBellPreview(n.body) : null;
              const previewTruncated =
                titlePrev.wasTruncated || (bodyPrev?.wasTruncated ?? false);
              return (
                <Dropdown.Item
                  key={n.id}
                  as="button"
                  className={`notification-dropdown-item text-start ${!n.readAt ? 'unread' : ''}`}
                  onClick={() => void handleItemActivate(n)}
                >
                  <div className="notification-dropdown-title">{titlePrev.short}</div>
                  {n.body && (
                    <p className="notification-dropdown-body mb-0">{bodyPrev!.short}</p>
                  )}
                  {previewTruncated && (
                    <p className="notification-dropdown-truncate-hint mb-0">
                      {t(
                        'notifications.previewTruncatedHint',
                        'Click to see the full message.',
                      )}
                    </p>
                  )}
                  <div className="notification-dropdown-meta">
                    {formatNotificationTimestamp(n.createdAt, i18n.language, n.category)}
                  </div>
                </Dropdown.Item>
              );
            })}
          {!loading && totalCount > 3 && (
            <div className="notification-dropdown-footer px-2">
              <Link
                to="/notifications"
                className="btn btn-sm btn-outline-primary w-100"
                onClick={() => setOpen(false)}
              >
                {t('notifications.viewAll', 'View all notifications')}
              </Link>
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default NotificationBell;
